import { Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  ROOTS_BOOKINGS_MANAGEMENT,
  bookingsManagementPath,
} from '@/features/bookings-management/routes/paths';
import {
  ROOTS_PROVIDER_MANAGEMENT,
  providersPath,
} from '@/features/users/providers/routes/paths';

type Props = {
  link?: string | null;
  bookingId?: number | string | null;
  sessionId?: number | string | null;
  providerId?: number | string | null;
};

const hasValue = (value: unknown) =>
  value !== null && value !== undefined && String(value).trim() !== '';

const INTERNAL_DASHBOARD_ROOTS = [
  ROOTS_BOOKINGS_MANAGEMENT,
  ROOTS_PROVIDER_MANAGEMENT,
];

const DASHBOARD_HOSTS = ['dashboard.getsetclean.co.uk'];

const splitPathSuffix = (href: string) => {
  const suffixIndex = href.search(/[?#]/);

  if (suffixIndex === -1) {
    return { pathname: href, suffix: '' };
  }

  return {
    pathname: href.slice(0, suffixIndex),
    suffix: href.slice(suffixIndex),
  };
};

const findNestedDashboardRootIndex = (pathname: string) =>
  INTERNAL_DASHBOARD_ROOTS.reduce((matchedIndex, root) => {
    let rootIndex = pathname.indexOf(root);

    while (rootIndex !== -1) {
      const nextChar = pathname[rootIndex + root.length];
      const isRootSegment = !nextChar || nextChar === '/';

      if (isRootSegment) {
        return rootIndex > 0 &&
          (matchedIndex === -1 || rootIndex < matchedIndex)
          ? rootIndex
          : matchedIndex;
      }

      rootIndex = pathname.indexOf(root, rootIndex + 1);
    }

    return matchedIndex;
  }, -1);

const normalizeNestedDashboardPath = (href: string) => {
  const { pathname, suffix } = splitPathSuffix(href);
  const dashboardRootIndex = findNestedDashboardRootIndex(pathname);

  return dashboardRootIndex > 0
    ? `${pathname.slice(dashboardRootIndex)}${suffix}`
    : href;
};

const canNormalizeDashboardUrl = (url: URL) => {
  const currentHost = typeof window === 'undefined' ? '' : window.location.host;

  return url.host === currentHost || DASHBOARD_HOSTS.includes(url.host);
};

const normalizeIssueHref = (link?: string | null) => {
  const href = typeof link === 'string' ? link.trim() : '';

  if (!href) return '';

  if (/^https?:\/\//i.test(href)) {
    try {
      const url = new URL(href);

      if (canNormalizeDashboardUrl(url)) {
        const pathname = normalizeNestedDashboardPath(url.pathname);

        if (pathname !== url.pathname) {
          url.pathname = pathname;
          return url.toString();
        }
      }
    } catch (_error) {
      return href;
    }

    return href;
  }

  if (/^[a-z][a-z\d+\-.]*:/i.test(href) || href.startsWith('//')) {
    return href;
  }

  if (href.startsWith('#') || href.startsWith('?')) {
    return href;
  }

  const rootRelativeHref = href.startsWith('/') ? href : `/${href}`;

  return normalizeNestedDashboardPath(rootRelativeHref);
};

const buildDashboardLink = ({
  bookingId,
  sessionId,
  providerId,
}: Pick<Props, 'bookingId' | 'sessionId' | 'providerId'>) => {
  if (hasValue(bookingId)) {
    return bookingsManagementPath('view', {
      bookingId: String(bookingId),
      bookingTabSid: 'booking-sessions',
      sessionFilter: hasValue(sessionId)
        ? `previous?sessionID=${String(sessionId)}`
        : 'today',
    });
  }

  if (hasValue(providerId)) {
    return providersPath('edit', {
      userId: String(providerId),
      tabSid: 'details',
    });
  }

  return '';
};

export default function InvoiceDryRunIssueLink({
  link,
  bookingId,
  sessionId,
  providerId,
}: Props) {
  const href = normalizeIssueHref(link);

  if (href) {
    return (
      <Link href={href} target="_blank" rel="noopener noreferrer">
        View
      </Link>
    );
  }

  const dashboardLink = buildDashboardLink({
    bookingId,
    sessionId,
    providerId,
  });

  if (!dashboardLink) {
    return <>No link</>;
  }

  return (
    <Link component={RouterLink} to={dashboardLink}>
      View
    </Link>
  );
}
